import React, { useMemo } from 'react';

import { Link } from 'react-router-dom';

import type { ColumnsType } from 'antd/es/table';

import { createAssetSearchFilter, mapTagsToBadgeItems } from '@/shared/lib/asset';
import { OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { openTagInNewTab } from '@/shared/lib/navigation';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { BadgeList } from '@/shared/ui/BadgeList';
import { DataTable } from '@/shared/ui/DataTable';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { filterEntityLabels } from '@/entities/search';

import type { Database } from '@/features/asset-detail/api/databasesApi';

import { SEARCH_PLACEHOLDERS } from '@/widgets/asset-detail/shared/constants';

import styles from './DatabasesTab.module.css';

type DatabasesTabProps = {
  databases: Database[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  onDeletedToggle: (includeDeleted: boolean) => void;
  includeDeleted: boolean;
};

type ColumnKey = 'name' | 'description' | 'owners' | 'tags';

const COLUMN_WIDTHS = {
  NAME: 200,
  DESCRIPTION: 352,
  TAGS_SMALL: 250,
  OWNERS: 352,
} as const;

const DatabasesTabComponent: React.FC<DatabasesTabProps> = ({
  databases,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  onDeletedToggle,
  includeDeleted,
}) => {
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const columns = useMemo<Record<ColumnKey, ColumnsType<Database>[number]>>(() => {
    const cols: Record<string, ColumnsType<Database>[number]> = {
      name: {
        title: '이름',
        dataIndex: 'name',
        key: 'name',
        width: COLUMN_WIDTHS.NAME,
        fixed: 'left',
        render: (name: string, record: Database) => (
          <Link to={`/database/${record.fullyQualifiedName}`} className={styles.databaseName}>
            {record.displayName || name}
          </Link>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      description: {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: databases.length > 0 ? COLUMN_WIDTHS.DESCRIPTION : 'auto',
        render: (description?: string) => {
          return <AssetDescription description={description} mode="expandable" />;
        },
      },
      tags: {
        title: '태그',
        dataIndex: 'tags',
        key: 'tags',
        width: COLUMN_WIDTHS.TAGS_SMALL,
        render: (tags: Database['tags']) => {
          const classificationTags = filterEntityLabels(tags, {
            includeOnlySource: 'Classification',
            requireConfirmed: true,
          });
          if (classificationTags.length === 0) return '-';
          return (
            <BadgeList
              items={mapTagsToBadgeItems(classificationTags)}
              onItemClick={openTagInNewTab}
              maxVisibleCount={5}
            />
          );
        },
      },
    };

    if (showOwners) {
      cols.owners = {
        title: '소유자',
        dataIndex: 'owners',
        key: 'owners',
        width: COLUMN_WIDTHS.OWNERS,
        render: (owners: Database['owners']) => {
          if (!owners || owners.length === 0) return '-';
          return <OwnerAvatars owners={owners} size={OWNER_AVATAR_SIZES.TABLE} />;
        },
      };
    }

    return cols as Record<ColumnKey, ColumnsType<Database>[number]>;
  }, [showOwners, databases.length]);

  const defaultColumnOrder = useMemo<ColumnKey[]>(() => {
    const order: ColumnKey[] = ['name', 'description'];
    if (showOwners) order.push('owners');
    order.push('tags');
    return order;
  }, [showOwners]);

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="데이터베이스 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  return (
    <div className={styles.databasesTab}>
      <DataTable
        data={databases}
        columns={columns}
        defaultColumnOrder={defaultColumnOrder}
        defaultVisibleColumns={defaultColumnOrder}
        searchPlaceholder={SEARCH_PLACEHOLDERS.DATABASES}
        searchFilter={createAssetSearchFilter}
        isLoading={isLoading}
        includeDeleted={includeDeleted}
        onDeletedToggle={onDeletedToggle}
        rowKey={(record) => record.id}
      />
    </div>
  );
};

export const DatabasesTab = React.memo(DatabasesTabComponent);
